import { useMemo } from 'react';

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({ currentPage, totalCount, pageSize, onPageChange }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);

  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(start + maxVisible - 1, totalPages);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages]);

  if (totalCount <= pageSize) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-background-200">
      <span className="text-xs text-foreground-400">
        {from.toLocaleString()}–{to.toLocaleString()} / 총 {totalCount.toLocaleString()}건
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-xs text-foreground-400 hover:bg-background-100 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-colors"
          title="처음"
        >
          <i className="ri-arrow-left-double-line"></i>
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-xs text-foreground-400 hover:bg-background-100 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-colors"
          title="이전"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>

        {pageNumbers[0] > 1 && (
          <span className="w-8 h-8 flex items-center justify-center text-xs text-foreground-300">...</span>
        )}

        {pageNumbers.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              p === currentPage
                ? 'bg-primary-500 text-white'
                : 'text-foreground-500 hover:bg-background-100'
            }`}
          >
            {p}
          </button>
        ))}

        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <span className="w-8 h-8 flex items-center justify-center text-xs text-foreground-300">...</span>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-xs text-foreground-400 hover:bg-background-100 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-colors"
          title="다음"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-xs text-foreground-400 hover:bg-background-100 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-colors"
          title="마지막"
        >
          <i className="ri-arrow-right-double-line"></i>
        </button>
      </div>
    </div>
  );
}